import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Container, Form, Modal, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { BiPlus, BiTrash } from "react-icons/bi";
import { axiosAuth_IDENT_MGMT_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import './AdminPages.css';
import configInfo from '../config/config.json';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';


const RoleAdminDetailsView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);

  // Initialize with actual JSON obj.  If not, will get error.
  const [roleAttrs, setRoleAttrs] = useState({
    id: '',
    name: '',
    nameLang2: '',
    nameLang3: '',
    description: '',
    creatorUsername: '',
    timeCreated: '',
    updaterUsername: '',
    timeUpdated: ''
  });

  const [currentRoleAttrs, setCurrentRoleAttrs] = useState({});

  const [permList, setPermList] = useState([]);

  const [permAddList, setPermAddList] = useState([]);
  const [unassignedPermsList, setUnassignedPermsList] = useState();
  const [showAddPermsDialog, setShowAddPermsDialog] = useState(false);


  // UI Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      setRoleAttrs(props.objDetails);
      setCurrentRoleAttrs(props.objDetails);

      fetchAssociatedPerms(props.objDetails.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);


  const fetchAssociatedPerms = (roleId) => {

    if (!roleId) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.get("roles/" + roleId + "/permissions");

        resolve(res.data);

        if (res.status === 200) {
          setPermList(res.data);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const fetchUnassignedPerms = () => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.get("permissions");

        resolve(res.data);

        if (res.status === 200) {
          // Remove permissions already assigned to the role
          let permIdArray = [];

          for (let i=0; i<permList.length; i++) {
            permIdArray.push(permList[i].id);
          }

          let unassignedPermsArray = [];

          for (let i=0; i<res.data.length; i++) {
            if (!permIdArray.includes(res.data[i].id)) {
              unassignedPermsArray.push(res.data[i]);
            }
          }

          setUnassignedPermsList(unassignedPermsArray);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
        return;
      }
    });
  }


  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);

  const [errMsg,           setErrMsg]           = useState('');
  const [updateRoleErrMsg, setUpdateRoleErrMsg] = useState('');
  const [addRoleErrMsg,    setAddPermsErrMsg]    = useState('');


  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }


  const onAttrsInputChange = (e) => {
    setRoleAttrs({ ...roleAttrs, [e.target.name]: e.target.value })
  }


  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setRoleAttrs(currentRoleAttrs);

    setEditMode(false);
    setUpdateRoleErrMsg('');
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    if (!roleAttrs.name || roleAttrs.name.trim().length === 0) {
      setUpdateRoleErrMsg(t("I18N_ERR_Need_name"));
    } else if (roleAttrs.name.length > 100) {
      setUpdateRoleErrMsg(t("I18N_Name") + " - " + t("I18N_ERR_Max_len") + "100");

    } else if (!roleAttrs.nameLang2 || roleAttrs.nameLang2.trim().length === 0) {
      setUpdateRoleErrMsg(t("I18N_ERR_Need_name") + " (" + t("custom:I18N_Language_2") + ")");
    } else if (roleAttrs.nameLang2.length > 100) {
      setUpdateRoleErrMsg(t("I18N_Name") + " (" + t("custom:I18N_Language_2") + ") - " + t("I18N_ERR_Max_len") + "100");

    } else if (!roleAttrs.nameLang3 || roleAttrs.nameLang3.trim().length === 0) {
      setUpdateRoleErrMsg(t("I18N_ERR_Need_name") + " (" + t("custom:I18N_Language_3") + ")");
    } else if (roleAttrs.nameLang3.length > 100) {
      setUpdateRoleErrMsg(t("I18N_Name") + " (" + t("custom:I18N_Language_3") + ") - " + t("I18N_ERR_Max_len") + "100");

    } else if (!roleAttrs.description || roleAttrs.description.trim().length === 0) {
      setUpdateRoleErrMsg(t("I18N_ERR_Need_description"));
    } else if (roleAttrs.description.length > 250) {
      setUpdateRoleErrMsg(t("I18N_ERR_Max_len_desc") + "250");

    } else {
      validateNames();
    }

    return;
  }

  // Since this is async, need to call this first, and check the others when username availability returns
  function validateNames() {

    new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.get("roles?currentRoleId=" + roleAttrs.id +
                                                       "&nameExist="      + roleAttrs.name +
                                                       "&nameLang2Exist=" + roleAttrs.nameLang2 +
                                                       "&nameLang3Exist=" + roleAttrs.nameLang3);

        resolve(res.data);

        if ((res.status === 200) && (res.data.length > 0)) {
          // Role code is taken
          setUpdateRoleErrMsg(t("I18N_ERR_RoleName_taken"));
          return;
        } else {
          validateOtherFields();
          return;
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setUpdateRoleErrMsg(t(translateAPIError(error)));
          validateOtherFields();
        }
        return;
      }
    });
  }

  function validateOtherFields() {
    // Validate form inputs

    updateRole();
  }

  const updateRole = () => {
    const reqBody = roleAttrs;

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.put("roles/" + roleAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          refreshRoleAttrs(roleAttrs.id);

          // Restore UI to viewing mode
          setEditMode(false);
          setUpdateRoleErrMsg('');
        }
      } catch (error) {
        setUpdateRoleErrMsg(t(translateAPIError(error)));
        return;
      }
    });

  }

  const refreshRoleAttrs = (id) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.get("roles/" + id);

        resolve(res.data);

        if (res.status === 200) {

          setRoleAttrs(res.data[0]);
          setCurrentRoleAttrs(res.data[0]);
          //setCurrentDeptAttrs(res.data[0]);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    props.onUIMode('LIST')
  }

  const handleAddPerms = (e) => {
    fetchUnassignedPerms();
    setShowAddPermsDialog(true);
  }

  function handleAddPermsClose() {
    setPermAddList([]);
    setShowAddPermsDialog(false);
  }

  const handlePermSelect = (e) => {

    if (e.target.checked) {
      // Add role to the list
      permAddList.push(e.target.id);
    } else {
      const index = permAddList.indexOf(e.target.id);
      if (index > -1) {
        permAddList.splice(index, 1);
      }
    }

    setPermAddList(permAddList);
  }

  function handleAddPermsSubmit() {
    let permIds = [];

    if (permAddList.length === 0) {
      setShowAddPermsDialog(false);
      return;
    }

    for (let i=0; i<permAddList.length; i++) {
      permIds.push(parseInt(permAddList[i]));
    }

    let reqBody = {
          permIds: permIds
        };

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_IDENT_MGMT_API.post("roles/" + roleAttrs.id + "/permissions", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setPermAddList([]);
          setAddPermsErrMsg('');
          setShowAddPermsDialog(false);
          fetchAssociatedPerms(roleAttrs.id);
        } else {
          // ##### NEED TO DECIDE WHAT TO DO #####
        }
      } catch (error) {
/*
        if (!error.message || (error.message !== 'IGNORE')) {
        }
*/
        setAddPermsErrMsg(t(translateAPIError(error)));
        return;
      }
    });
  }


  function renderAddPerms(perm, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td><Form.Check type="checkbox" id={perm.id} label="" onChange={handlePermSelect} /></td>
        <td>{(t("custom:I18N_" + perm.nameCode) === ("I18N_" + perm.nameCode)) ?
              perm.nameCode : t("custom:I18N_" + perm.nameCode)}</td>
        <td>{perm.description}</td>
      </tr>
    )
  }


  function handleRemovePerm(perm) {

    let reqBody = {
          data: {
             "permIds": []
          }
        };

    reqBody.data.permIds.push(perm.id);

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_IDENT_MGMT_API.delete("roles/" + roleAttrs.id + "/permissions/", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setErrMsg('');
          fetchAssociatedPerms(roleAttrs.id);
        } else {
          // ##### NEED TO DECIDE WHAT TO DO #####
        }
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
        return;
      }
    });
  }


  function renderPerm(perm, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td style={{width:"25%"}}>{perm.nameCode}</td>
        <td style={{width:"70%"}}>{perm.description}</td>
        <td style={{width:"5%"}}>
          <OverlayTrigger overlay={<Tooltip id="tooltip-remove-screen" style={{fontSize:'11px'}}>{t("I18N_TIP_Delete_perm_from_role")}</Tooltip>}>
            <span style={{cursor:'pointer', color:'SteelBlue'}}
                  onClick={() => handleRemovePerm(perm)}><BiTrash size={16} />
            </span>
          </OverlayTrigger>
        </td>
      </tr>
    )
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_Role_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_Role_info")}
            </span>
            {editMode || !configInfo.appConfig.isPrimaryIdentProvider ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Role_name")}: *</Col>
                  { configInfo.uiConfig.visibleFields.ROLE_NameLang2 ?
                    <Col>{t("I18N_Role_name") + " (" + t("custom:I18N_Language_2") + ")"}: *</Col>
                  : null
                  }
                  { configInfo.uiConfig.visibleFields.ROLE_NameLang3 ?
                    <Col>{t("I18N_Role_name") + " (" + t("custom:I18N_Language_3") + ")"}: *</Col>
                  : null
                  }
                  <Col>{t("I18N_Description")}: *</Col>
                  { !configInfo.uiConfig.visibleFields.ROLE_NameLang2 ?
                    <Col></Col>
                  : null
                  }
                  { !configInfo.uiConfig.visibleFields.ROLE_NameLang3 ?
                    <Col></Col>
                  : null
                  }
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="name" value={roleAttrs.name}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  { configInfo.uiConfig.visibleFields.ROLE_NameLang2 ?
                    <Col>
                      <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="nameLang2" value={roleAttrs.nameLang2}
                        onChange={(e) => onAttrsInputChange(e)} />
                    </Col>
                  : null
                  }
                  { configInfo.uiConfig.visibleFields.ROLE_NameLang3 ?
                    <Col>
                      <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="nameLang3" value={roleAttrs.nameLang3}
                        onChange={(e) => onAttrsInputChange(e)} />
                    </Col>
                  : null
                  }
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} style={{width:'100%'}} name="description" value={roleAttrs.description}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  { !configInfo.uiConfig.visibleFields.ROLE_NameLang2 ?
                    <Col></Col>
                  : null
                  }
                  { !configInfo.uiConfig.visibleFields.ROLE_NameLang3 ?
                    <Col></Col>
                  : null
                  }
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {updateRoleErrMsg}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_Role_name")}:</Col>
                { configInfo.uiConfig.visibleFields.ROLE_NameLang2 ?
                  <Col>{t("I18N_Role_name") + " (" + t("custom:I18N_Language_2") + ")"}:</Col>
                : null
                }
                { configInfo.uiConfig.visibleFields.ROLE_NameLang3 ?
                  <Col>{t("I18N_Role_name") + " (" + t("custom:I18N_Language_3") + ")"}:</Col>
                : null
                }
                <Col>{t("I18N_Description")}:</Col>
                { !configInfo.uiConfig.visibleFields.ROLE_NameLang2 ?
                  <Col></Col>
                : null
                }
                { !configInfo.uiConfig.visibleFields.ROLE_NameLang3 ?
                  <Col></Col>
                : null
                }
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{roleAttrs.name}</Col>
                { configInfo.uiConfig.visibleFields.ROLE_NameLang2 ?
                  <Col>{roleAttrs.nameLang2}</Col>
                : null
                }
                { configInfo.uiConfig.visibleFields.ROLE_NameLang3 ?
                  <Col>{roleAttrs.nameLang3}</Col>
                : null
                }
                <Col>
                  <textarea readOnly className={classes.admin_pages__field_editable} style={{width:'100%'}} name="description" value={roleAttrs.description} />
                </Col>
                { !configInfo.uiConfig.visibleFields.ROLE_NameLang2 ?
                  <Col></Col>
                : null
                }
                { !configInfo.uiConfig.visibleFields.ROLE_NameLang3 ?
                  <Col></Col>
                : null
                }
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{roleAttrs.creatorUsername}</Col>
                <Col>{formatDate(roleAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{roleAttrs.updaterUsername}</Col>
                <Col>{formatDate(roleAttrs.timeUpdated)}</Col>
              </Row>
            </Container>
          }
        </div>



        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'30px'}}>
          <div style={{display:'flex'}}>
            <span>
              {t("I18N_Perms_assigned_to_role")}
            </span>
            <span style={{marginLeft:'auto', marginRight:'20px'}}>
              <OverlayTrigger overlay={<Tooltip id="tooltip-new_role" style={{fontSize:'11px'}}>{t("I18N_TIP_Add_perm_to_role")}</Tooltip>}>
                <span onClick={handleAddPerms} style={{ marginLeft: '0px', marginRight: '0px', marginTop: '3px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                  <BiPlus size={18} />
                </span>
              </OverlayTrigger>
            </span>
          </div>
        </div>

        <hr className="solid" style={{ margin: '0 0 0 0' }}></hr>

        <div style={{overflow:'auto', fontSize:'11px', marginTop:'10px', maxHeight:'500px'}}>

          <Table hover responsive size="sm">
            <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
              <tr>
                <th style={{width:"25%", fontWeight:'400'}}>{t("I18N_Perm_code")}</th>
                <th style={{width:"70%", fontWeight:'400'}}>{t("I18N_Description")}</th>
                <th style={{width:"5%", fontWeight:'400'}}></th>
              </tr>
            </thead>
            <tbody>
              {permList.map(renderPerm)}
            </tbody>
          </Table>

        </div>

        <div className={classes.admin_pages__page_subtitle}>
          <Form.Group as={Row} className="mb-2 mt-3">
            <Col sm="2">
              <Button size="sm" variant="outline-secondary"
                style={{ fontSize:'12px', fontWeight:'300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
            </Col>
            <Col sm="10">
              <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                {errMsg}
              </span>
            </Col>
          </Form.Group>
        </div>

        <Modal show={showAddPermsDialog} onHide={handleAddPermsClose} centered >
          <Modal.Body style={{height:'700px', overflow:'auto'}}>

            <div className={classes.admin_pages__page_subtitle} style={{marginTop:'0px'}}>
              <div display='flex'>
                <span>
                  {t("I18N_Permissions")}
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>
            </div>

            <div style={{height:'590px', overflow:'auto', fontSize:'11px', marginTop:'10px'}}>
              <Table hover responsive size="sm">
                <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                  <tr>
                    <th></th>
                    <th style={{fontWeight:'400'}}>{t("I18N_Perm_code")}</th>
                    <th style={{fontWeight:'400'}}>{t("I18N_Description")}</th>
                  </tr>
                </thead>
                <tbody>
                  {unassignedPermsList? unassignedPermsList.map(renderAddPerms) : null}
                </tbody>
              </Table>
            </div>

            <div display='flex' style={{ marginTop: '15px' }}>
              <Button size="tiny" variant="outline-secondary" className="mb-3"
                style={{ float: 'left' }}
                onClick={handleAddPermsClose}>
                {t("I18N_Cancel")}
              </Button>
              <Button size="tiny" variant="outline-secondary" className="mb-3"
                style={{ marginLeft: '15px' }}
                onClick={handleAddPermsSubmit}>
                {t("I18N_Add_perms")}
              </Button>
              <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {addRoleErrMsg}
              </span>
            </div>
          </Modal.Body>
        </Modal>

      </Card>
    </>
  );
}
 
export default RoleAdminDetailsView;
